import { Component, OnInit, ViewChild } from '@angular/core';
import { Plant } from 'app/shared/models/plant.model';
import { PlantService } from 'app/core/services/plant.service';
import { PlantCreateModalComponent } from './components/plant-create-modal/plant-create-modal.component';
import { Line } from "app/shared/models/line.model";
import { ToastsManager } from "@app/shared/util/toastr.service";
import { TranslateParser, TranslateService } from '@ngx-translate/core';
import { TableConfig } from 'app/shared/util/table-config.utils';
import { TableBasedComponent } from "app/shared/interface/TableBasedComponent";
import { Log } from "ng2-logger";
import { PermissionService } from "@app/authentication/services/permission.service";
import { AppUtils } from "@app/shared/util/app.utils";
import {
    DocumentsLocationConfirmationModalComponent
} from './components/documents-location-confirmation-modal/documents-location-confirmation-modal.component';
import { ToolService } from '@app/core/services/tool.service';

@Component({
    selector: 'app-plants',
    templateUrl: './plants.component.html',
    styleUrls: ['./plants.component.scss']
})
export class PlantManagementComponent implements OnInit, TableBasedComponent<Plant> {
    _logger = Log.create(PlantManagementComponent.name);

    @ViewChild(PlantCreateModalComponent, {static: true}) plantFormModal: PlantCreateModalComponent;
    @ViewChild(DocumentsLocationConfirmationModalComponent, {static: true}) confirmationModal: DocumentsLocationConfirmationModalComponent;

    lines: Line[] = [];
    plants: Plant[] = [];
    selectedPlant: Plant = new Plant();
    tableConfig = new TableConfig();
    isLoadingPlants = false;

    constructor(private plantService: PlantService,
                private toolService: ToolService,
                private toastr: ToastsManager,
                private translationService: TranslateService,
                private permissionService: PermissionService,
                private translateParser: TranslateParser) {
    }

    ngOnInit() {
        this._logger.info("init()");
        this.getAll();
    }

    openPlantModal() {
        this.selectedPlant = new Plant();
        this.plantFormModal.open();
    }

    onDuplicateRow(plant) {
        this._logger.info('onDuplicateRow::', plant);
        plant.name = plant.name + AppUtils.COPY_STRING;
        this.plantFormModal.open(plant, true);
    }

    getAll() {
        this._logger.info("getAll(): Retrieving list of plants");
        this.isLoadingPlants = true;
        this.plantService.getAllPlants(this.tableConfig).subscribe(
            (data: any) => {
                this._logger.info("getAll(): API returned", data);
                this.plants = data.items;
                this.tableConfig.pager.count = data.total;
                this.isLoadingPlants = false;
            },
            (error: any) => {
                this._logger.info("getAll(): Error retrieving data", error);
                this.isLoadingPlants = false;
            }
        );
    }

    create(plant: Plant) {
        this.isLoadingPlants = true;
        this.plantService.createPlant(plant).subscribe(
            (data: any) => {
                this.getAll();
                this.toastr.success(this.buildCreateMessage(data.name));
                this.isLoadingPlants = false;
                this.plantFormModal.modal.close();
            },
            (error: any) => {
                this._logger.info("getAll(): Error retrieving data", error);
                this.isLoadingPlants = false;
            }
        );
    }

    remove(plant: Plant) {
        this.isLoadingPlants = true;
        this.plantService.deletePlant(plant).subscribe(
            (data: any) => {
                this.getAll();
                this.toastr.warning(this.buildDeleteMessage(plant.name));
                this.isLoadingPlants = false;
                this.plantFormModal.close();
            },
            (error: any) => {
                this._logger.info("getAll(): Error retrieving data", error);
                this.isLoadingPlants = false;
            }
        );
    }

    update(plantUpdate) {
        this.isLoadingPlants = true;
        this.plantService.updatePlant(plantUpdate.newPlant).subscribe(
            (data: any) => {
                const documentsLocationChanged = this.plants.find(p => p.id === data.id)
                    .documentsLocation !== this.plantFormModal.getFormValue().documentsLocation;
                this.getAll();
                this.isLoadingPlants = false;
                this.plantFormModal.close();
                if (documentsLocationChanged && data.isNewPathValid) {
                    this.getRemovedDocuments(data.id, plantUpdate.previousPlantName);
                } else {
                    if (documentsLocationChanged && !data.isNewPathValid) {
                        this.toastr.error(this.translationService.instant('admin.plants.toasts.invalidPath'));
                    } else {
                        this.toastr.success(this.buildUpdateMessage(plantUpdate.previousPlantName));
                    }
                }
            },
            (error: any) => {
                this._logger.info("getAll(): Error retrieving data", error);
                this.isLoadingPlants = false;
            }
        );
    }

    getRemovedDocuments(plantId: number, previousPlantName: string) {
        this.toolService.getRemovedDocuments(plantId, this.plantFormModal.getFormValue().documentsLocation).subscribe(
            (paths: string[]) => {
                if (paths.length > 0) {
                    this.confirmationModal.open(paths, {
                        plantId: plantId,
                        newPath: this.plantFormModal.getFormValue().documentsLocation,
                        previousPlantName: previousPlantName
                    });
                } else {
                    this.onConfirm({
                        plantId: plantId,
                        newPath: this.plantFormModal.getFormValue().documentsLocation,
                        pathsToBeRemoved: [],
                        previousPlantName: previousPlantName
                    });
                }
            },
            (error) => {
                this.toastr.error(error.error.message);
            }
        );
    }

    onConfirm(event) {
        this.plantService.updatePlantDocumentsLocation(
            event.plantId,
            event.newPath,
            event.pathsToBeRemoved,
            this.selectedPlant.documentsLocation
        ).subscribe(
            (data) => {
                this.toastr.success(this.buildUpdateMessage(event.previousPlantName));
                this.getAll();
            },
            (error) => {
                this.toastr.error(error.error.message);
            }
        );
    }

    onSortChange(event) {
        this.getAll();
    }

    onPageChange(event) {
        this.getAll();
    }

    onFilter(event) {
        this.getAll();
    }

    onTableRowClick(plant: Plant) {
        this.plantFormModal.open(plant);
        this.selectedPlant = plant;
    }

    buildCreateMessage(plantName) {
        const message = this.translationService.instant('admin.plants.toasts.created');
        return this.translateParser.interpolate(message, {plantName: plantName});
    }

    buildUpdateMessage(plantName) {
        const message = this.translationService.instant('admin.plants.toasts.updated');
        return this.translateParser.interpolate(message, {plantName: plantName});
    }

    buildDeleteMessage(plantName) {
        const message = this.translationService.instant('admin.plants.toasts.deleted');
        return this.translateParser.interpolate(message, {plantName: plantName});
    }

    shouldRenderModifiers() {
        return this.permissionService.hasHasPermissionForFeature('MANAGE_PLANTS');
    }

}


